// shared-lib/dimensioning/records.js
//
// Editing `room.dimensions[]` — the stated dimension records.
//
// PURE and id-deterministic: a record's id IS its dedupe key
// (`<subject refKey>|<rule>`), so restating the same intent replaces the old
// record instead of stacking a twin, and nothing here needs a uuid or a clock.
//
// See claude-docs/2026-09-15-unified-dimensioning.md §3, §8.

const { normalizeRef, refKey } = require('../group-customization/refs');
const { DIMENSION_RULES } = require('./rules');

const safeRefKey = (ref) => {
  try {
    return refKey(normalizeRef(ref));
  } catch {
    return null;
  }
};

/**
 * What a record is a statement ABOUT, beyond its subject and rule.
 *
 * Without this, every `centered-between` on one object shared an id, so
 * stating "centre top to bottom" REPLACED "centre left to right" — and an
 * object cannot be centred on both axes, which is the ordinary thing to want
 * for a ceiling fixture.
 *
 * The DATUM is the scope, not the axis: centring left-to-right names the
 * east/west walls and top-to-bottom names the north/south pair, so the datum
 * already separates them — and no two axes can share a boundary pair. Keying
 * on the datum also avoids an identity that changes depending on whether a
 * derivable `axis` happened to be stored. The axis is the fallback for a
 * record that names no datum at all.
 */
function recordScope(record) {
  const refs = Array.isArray(record?.datum) ? record.datum : [record?.datum];
  const keys = refs.map(safeRefKey).filter(Boolean);
  if (keys.length) return keys.join('+');
  return record?.axis || null;
}

/** `<subject>|<rule>|<axis or datum>` — the identity a restatement replaces. */
function dimensionRecordId(record) {
  if (!record?.rule) return null;
  const key = safeRefKey(record.subject);
  if (!key) return null; // an unknown ref kind is not an identity
  const scope = recordScope(record);
  return scope ? `${key}|${record.rule}|${scope}` : `${key}|${record.rule}`;
}

/**
 * Why this record may not be stated, or null when it may. PURE.
 *
 * A cabinet run owns the position of every object in its bays, so centring one
 * in the room — or enforcing any dimension on it — is a contradiction the run
 * would win at its next re-solve. Refusing at authoring time is the whole
 * point: left to the re-solve, the user's statement disappears silently.
 *
 * @param {Object} record
 * @param {Object} entity  the subject as stored (may carry runId / bayId).
 */
function dimensionRecordRefusal(record, entity) {
  if (!record?.rule) return 'no rule';
  if (!DIMENSION_RULES[record.rule]) return `unknown rule: ${record.rule}`;
  if (!dimensionRecordId(record)) return 'no subject';

  const inBay = !!(entity?.bayId || entity?.runId);
  if (inBay && (record.rule === 'centered-between' || record.enforced)) {
    return 'a cabinet run owns this object’s position';
  }
  return null;
}

/** True when the subject may carry this rule at all — the UI gate. */
const canStateDimension = (record, entity) =>
  dimensionRecordRefusal(record, entity) === null;

/**
 * Add a record, or replace the one that states the same thing. PURE.
 * Returns the array unchanged when the record is refused, so a caller that
 * skips the check cannot write an invalid one.
 */
function upsertDimensionRecord(records, record, entity) {
  const list = Array.isArray(records) ? records : [];
  if (dimensionRecordRefusal(record, entity)) return list;
  const id = dimensionRecordId(record);
  const stamped = { ...record, id };
  const at = list.findIndex((r) => r?.id === id);
  if (at === -1) return [...list, stamped];
  const next = [...list];
  next[at] = stamped;
  return next;
}

/** Drop a record by id. PURE. */
function removeDimensionRecord(records, id) {
  const list = Array.isArray(records) ? records : [];
  return list.filter((r) => r?.id !== id);
}

/** Every record stating something about one subject. PURE. */
function recordsForSubject(records, subjectRef) {
  const list = Array.isArray(records) ? records : [];
  const key = safeRefKey(subjectRef);
  if (!key) return [];
  return list.filter((r) => safeRefKey(r?.subject) === key);
}

/**
 * Drop records whose subject no longer exists. PURE.
 *
 * The same shape as `pruneDanglingRoomLinks` does for links, and for the same
 * reason: a reprocess replaces `objects` wholesale, and a record pointing at
 * an id that is gone can never draw again.
 */
function pruneDanglingRecords(records, liveIds) {
  const list = Array.isArray(records) ? records : [];
  const ids = liveIds instanceof Set ? liveIds : new Set(liveIds || []);
  return list.filter((r) => {
    let ref = null;
    try {
      ref = normalizeRef(r?.subject);
    } catch {
      return false;
    }
    if (!ref) return false;
    return ref.kind === 'object' ? ids.has(ref.id) : true;
  });
}

module.exports = {
  dimensionRecordId,
  dimensionRecordRefusal,
  canStateDimension,
  upsertDimensionRecord,
  removeDimensionRecord,
  recordsForSubject,
  pruneDanglingRecords,
};
